import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createTrip } from '../../../services/firestore/trips';
import { useAuth } from '../../../contexts/AuthContext';
import { uploadImage } from '../../../services/storage';
import { ArrowRight, ArrowLeft, X, Check, CheckCircle, ExternalLink } from 'lucide-react';
import { STEPS } from '../components/listing/constants';
import { useListingForm } from '../components/listing/useListingForm';
import StepBasicInfo from '../components/listing/StepBasicInfo';
import StepDescription from '../components/listing/StepDescription';
import StepPhotos from '../components/listing/StepPhotos';
import StepAmenities from '../components/listing/StepAmenities';
import StepPricing from '../components/listing/StepPricing';
import StepAvailability from '../components/listing/StepAvailability';
import StepPackages from '../components/listing/StepPackages';
import StepInstructorInfo from '../components/listing/StepInstructorInfo';
import StepReviews from '../components/listing/StepReviews';
import StepReview from '../components/listing/StepReview';

const CreateListing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const { formData, setFormData, handleInputChange, toggleAmenity, validateStep } = useListingForm();
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [publishStatus, setPublishStatus] = useState('draft');
  const [createdTripId, setCreatedTripId] = useState(null);
  const isApprovedHost = user?.approvedAsHost === true;

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (photoFiles.length + files.length > 10) {
      setError('Maximum 10 photos allowed');
      return;
    }
    
    const newFiles = [...photoFiles, ...files];
    setPhotoFiles(newFiles);
    
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreviews((prev) => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    const newFiles = photoFiles.filter((_, i) => i !== index);
    const newPreviews = photoPreviews.filter((_, i) => i !== index);
    setPhotoFiles(newFiles);
    setPhotoPreviews(newPreviews);
  };

  const handleNext = () => {
    const validation = validateStep(currentStep);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    
    // Special validation for photos step
    if (currentStep === 3 && photoFiles.length === 0) {
      setError('Please add at least one photo');
      return;
    }
    
    setError('');
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setError('');
  };

  const handlePreview = async () => {
    if (!user) return;
    setError('');
    setLoading(true);
    setUploadingPhotos(true);

    try {
      const uploadedPhotos = [];
      for (const file of photoFiles) {
        const path = `trips/${user.uid}/${Date.now()}_${file.name}`;
        const url = await uploadImage(file, path);
        uploadedPhotos.push(url);
      }

      const tripId = await createTrip({
        hostId: user.uid,
        ...formData,
        price: formData.basePrice || formData.price || 0,
        photos: uploadedPhotos,
        status: 'draft',
      });

      window.open(`/trips/${tripId}`, '_blank');
      setLoading(false);
      setUploadingPhotos(false);
    } catch (err) {
      setError(err.message || 'Failed to create preview');
      setLoading(false);
      setUploadingPhotos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    setError('');
    setLoading(true);
    setUploadingPhotos(true);

    try {
      const uploadedPhotos = [];
      for (const file of photoFiles) {
        const path = `trips/${user.uid}/${Date.now()}_${file.name}`;
        const url = await uploadImage(file, path);
        uploadedPhotos.push(url);
      }

      const finalStatus = isApprovedHost ? publishStatus : 'draft';

      const tripId = await createTrip({
        hostId: user.uid,
        ...formData,
        price: formData.basePrice || formData.price || 0,
        photos: uploadedPhotos,
        status: finalStatus,
      });

      setCreatedTripId(tripId);
      setTimeout(() => {
        navigate('/host/trips');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to create listing');
      setLoading(false);
      setUploadingPhotos(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <StepBasicInfo formData={formData} handleInputChange={handleInputChange} />;
      case 2:
        return <StepDescription formData={formData} handleInputChange={handleInputChange} />;
      case 3:
        return (
          <StepPhotos
            photoFiles={photoFiles}
            photoPreviews={photoPreviews}
            existingPhotos={[]}
            handlePhotoSelect={handlePhotoSelect}
            removePhoto={removePhoto}
          />
        );
      case 4:
        return <StepAmenities formData={formData} toggleAmenity={toggleAmenity} />;
      case 5:
        return <StepPricing formData={formData} handleInputChange={handleInputChange} />;
      case 6:
        return <StepAvailability formData={formData} handleInputChange={handleInputChange} />;
      case 7:
        return <StepPackages formData={formData} handleInputChange={handleInputChange} />;
      case 8:
        return <StepInstructorInfo formData={formData} handleInputChange={handleInputChange} />;
      case 9:
        return <StepReviews formData={formData} handleInputChange={handleInputChange} />;
      case 10:
        return (
          <StepReview
            formData={formData}
            photoPreviews={photoPreviews}
            existingPhotos={[]}
            publishStatus={publishStatus}
            setPublishStatus={setPublishStatus}
            isApprovedHost={isApprovedHost}
            onPreview={handlePreview}
            loading={loading || uploadingPhotos}
          />
        );
      default:
        return null;
    }
  };

  const completedSteps = currentStep - 1;
  const progress = (completedSteps / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
      <div className="flex h-screen">
        {/* Sidebar */}
        <div className="hidden lg:flex w-80 bg-white border-r border-gray-200 flex-col">
          <div className="p-6 border-b border-gray-200">
            <button
              onClick={() => navigate('/host/trips')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
            >
              <X className="w-5 h-5" />
              <span className="text-sm font-medium">Exit</span>
            </button>
            <h2 className="text-2xl font-light text-gray-900 mb-2">Create Listing</h2>
            <p className="text-sm text-gray-600">Step {currentStep} of {STEPS.length}</p>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {STEPS.map((step, index) => {
                const StepIcon = step.icon;
                const stepNumber = index + 1;
                const isCompleted = stepNumber < currentStep;
                const isCurrent = stepNumber === currentStep;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => {
                      setCurrentStep(stepNumber);
                      setError('');
                    }}
                    className={`w-full flex items-start gap-4 p-4 rounded-lg transition-all text-left ${
                      isCurrent
                        ? 'bg-emerald-50 border-2 border-emerald-500'
                        : isCompleted
                        ? 'bg-emerald-50/30 border-2 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300'
                        : 'border-2 border-transparent hover:bg-gray-50 hover:border-gray-200'
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        isCompleted
                          ? 'bg-emerald-600 text-white'
                          : isCurrent
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`text-sm font-medium ${
                          isCurrent ? 'text-emerald-900' : isCompleted ? 'text-gray-900' : 'text-gray-500'
                        }`}
                      >
                        {step.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{step.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-6 border-t border-gray-200">
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 text-center">
              {Math.round(progress)}% Complete
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            {/* Mobile Header */}
            <div className="lg:hidden mb-6">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => navigate('/host/trips')}
                  className="p-2 text-gray-600 hover:text-gray-900"
                >
                  <X className="w-6 h-6" />
                </button>
                <span className="text-sm text-gray-600">Step {currentStep} of {STEPS.length}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            <div className="mb-8">
              <h1 className="text-4xl font-light text-gray-900 mb-2">
                {STEPS[currentStep - 1].title}
              </h1>
              <p className="text-lg text-gray-600 font-light">
                {STEPS[currentStep - 1].description}
              </p>
            </div>

            <form onSubmit={currentStep === STEPS.length ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 sm:p-10">
                {error && (
                  <div className="rounded-lg bg-red-50 border-2 border-red-300 p-4 mb-6">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                  </div>
                )}

                {createdTripId && (
                  <div className="rounded-lg bg-emerald-50 border-2 border-emerald-300 p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-emerald-800 mb-1">Listing created successfully!</p>
                        <p className="text-xs text-emerald-700">Redirecting to your listings...</p>
                      </div>
                      <a
                        href={`/trips/${createdTripId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View as Client
                      </a>
                    </div>
                  </div>
                )}

                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center pt-6 mt-8 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>

                  {currentStep < STEPS.length ? (
                    <button
                      type="submit"
                      className="inline-flex items-center gap-2 group bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-4 rounded-lg text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300"
                    >
                      Next
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading || uploadingPhotos}
                      className="inline-flex items-center gap-2 group bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-4 rounded-lg text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading || uploadingPhotos ? (
                        'Creating...'
                      ) : (
                        <>
                          {publishStatus === 'published' ? 'Publish Listing' : 'Save as Draft'}
                          <CheckCircle className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateListing;
